import React from "react";
import PropTypes from "prop-types";
import { defaultTemplate } from "core/hoc";
import { handleKeyDown, handlePaste, toUTCDate } from "core/utils";
import { Button } from "@progress/kendo-react-buttons";
import { BaasicButton, BaasicDropdown, BaasicConfirmModal } from "core/components";
import { NikoPatientTemplateClinicalEdit } from "application/niko-patient/components";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { observer } from "mobx-react";

const TooltipContent = observer(({ initianValue, history, fieldName }) => {
  if (!history || history.fieldName !== fieldName) return null;
  const date = new Date(history.eventDate + "Z");
  const formattedDate = date.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).replace(',', '');
  return (<div>
    <div>Previous: {initianValue ? initianValue : 'blank'}</div>
    <div style={{ fontSize: '11px', opacity: 0.75 }}>{history ? history.userName + ', ' + formattedDate : '-'}</div>
  </div>);
});

function NikoPatientTemplateListTemplate({ nikoPatientTemplateListViewStore }) {
  const {
    activeTemplates,
    inactiveTemplates,
    disabledItem,
    pageViewStore,
    openNikoTemplateCreateModal,
    showInactiveTemplates,
    changeInactiveTemplatesVisibility,
  } = nikoPatientTemplateListViewStore;

  return <React.Fragment>
    <div className="card--tertiary__niko u-flex--justify-end-text-center" style={{ paddingRight: "0px" }}>
      <BaasicButton
        className="btn btn--base-plus btn--light"
        icon="u-icon u-icon--xmed u-icon--add-lined"
        label="Add Template"
        onClick={() => openNikoTemplateCreateModal()}
        disabled={pageViewStore.disabled || disabledItem}
      />
    </div>
    {activeTemplates && activeTemplates.map((patientTemplate) => { return <NikoPatientTemplate nikoPatientTemplateListViewStore={nikoPatientTemplateListViewStore} patientTemplate={patientTemplate} key={patientTemplate.id} /> })}
    {inactiveTemplates && inactiveTemplates.length > 0 &&
      <React.Fragment >
        <a className="u-anchor--show-more u-mar--bottom--xlrg" onClick={() => changeInactiveTemplatesVisibility()}> {showInactiveTemplates ? "Hide" : "Show"} Inactive Templates</a>
        {showInactiveTemplates && inactiveTemplates.map((patientTemplate) => {
          return <NikoPatientTemplate nikoPatientTemplateListViewStore={nikoPatientTemplateListViewStore} patientTemplate={patientTemplate} key={patientTemplate.id} />
        })}
      </React.Fragment>
    }
  </React.Fragment>
}

NikoPatientTemplateListTemplate.propTypes = {
  nikoPatientTemplateListViewStore: PropTypes.object,
};

const NikoPatientTemplate = observer(({ nikoPatientTemplateListViewStore, patientTemplate }) => {
  const {
    saveItemOnProductSelect,
    productStore,
    editItem,
    updateItem,
    addEmptyItem,
    disabledItem,
    openFacilityOrderTemplateNewFlowDeleteModal,
    setItemToggle,
    openPatientTemplateMissingProductNoteModal,
    changeDiscontinuedStatus,
    pageViewStore,
    openNikoPatientTemplateItemCalculationModal,
    openPatientTemplateDoNotSupplyModal,
    templateChangeConfirmModal,
    swapPatientTemplateItem,
    insertImportActionId,
    deleteImportActionId,
    setAction,
    updateImportActionId,
    deletePatientTemplateItem,
    getFieldHistory,
    showItemQuantities,
    templateExpanded,
    } = nikoPatientTemplateListViewStore;



  const isExpanded = () => {
    return patientTemplate.id === templateExpanded.id && templateExpanded.expanded === true;
  }

  const showItemQuantitiesColumn = () => {
    return (isExpanded() || patientTemplate.isDifferentQty) && patientTemplate.patientTemplateItems.length > 0;
  }

  const tableColSpan = () => {
    return showItemQuantitiesColumn() ? 7 : 6;
  }

  const productDropdownItemRender = (li, itemProps) => {
    const itemName = itemProps && itemProps.dataItem.description;
    const isPreferredIcon = itemProps && itemProps.dataItem.isPreferred && itemProps.dataItem.itemCategory === "ostomy" ?
      <i className="u-icon u-icon--xsml u-icon--star-outlined u-mar--right--tny"></i> : null;
    const isBackorderedIcon = itemProps && itemProps.dataItem.backordered ? <i className="u-icon u-icon--sml u-icon--backordered u-mar--left--tny"></i> : null;
    const nonBillableIcon = itemProps && !itemProps.dataItem.billableToMedicare ? <i className="u-icon u-icon--sml u-icon--non-billable u-mar--left--tny"></i> : null;
    const itemChildren = (
      <span style={{ display: "flex", flexDirection: "column", width: "100%", }}>
        <span style={{ flex: "1" }}> {li.props.children} </span>
        <span style={{ fontSize: "12px" }}> {isPreferredIcon} {itemName} {nonBillableIcon} {isBackorderedIcon}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const dropdownRef = React.createRef();

  return (
    <div className="table--secondary__wrapper u-mar--bottom--sml">
      <ReactTooltip positionStrategy="fixed" id={"patientTemplateTooltip" + patientTemplate.id} place="bottom" />
      <div>
        <div className="table--secondary__add-anchor">
        <table width="100%" className="table--secondary">
          <colgroup>
            <col width={400} />
            <col width={150} />
            <col width={80} />
            <col width={70} />
            {showItemQuantitiesColumn() && <col width={70} />}
            <col />
            <col width={70} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={tableColSpan()} className="btn--group__wrapper">
                <div className="u-flex--space-between">
                  <label>
                    {(patientTemplate.subCategory && patientTemplate.subCategory.name.toUpperCase()) || ""}
                  </label>
                  {patientTemplate.isDiscontinued ?
                    <div>
                      <ReactTooltip positionStrategy="fixed" id={"discontinueTemplate"} place="top" />
                      <div className="u-mar--left--xsml btn--disc__wrapper" data-tooltip-content={new Date(patientTemplate.dateDiscontinued).toLocaleDateString("en-US", { year: "2-digit", month: "2-digit", day: "2-digit", })} data-tooltip-id={"discontinueTemplate"}>
                        Discontinued
                        <Button className="btn btn--disc" onClick={() => { changeDiscontinuedStatus(patientTemplate) }} disabled={pageViewStore.disabled}>
                          <i className="u-icon u-icon--xxsml u-icon--close--neg"></i>
                        </Button>
                      </div>
                    </div>
                    :
                    pageViewStore.nikoPatientDetail.status == 'Hospital' ?
                      <div className="u-mar--left--sml status-tag status-tag--light">
                        <span> PT {`${pageViewStore.nikoPatientDetail.status.toUpperCase()}IZED`} </span>
                      </div>
                      :
                      pageViewStore.nikoPatientDetail.status != 'Active' &&
                      <React.Fragment>
                        {pageViewStore.nikoPatientDetail.inactiveDate && <ReactTooltip positionStrategy="fixed" id={"patientStatus" + patientTemplate.id} place="top" />}
                        <div className="u-mar--left--sml status-tag" data-tooltip-content={toUTCDate(pageViewStore.nikoPatientDetail.inactiveDate, false, "MM/DD/YY")} data-tooltip-id={"patientStatus" + patientTemplate.id}>
                          <span> PT {pageViewStore.nikoPatientDetail.status.toUpperCase()} </span>
                        </div>
                      </React.Fragment>
                  }
                  {patientTemplate.isUserDns &&
                    <div>
                      <div className="u-mar--left--xsml btn--disc__wrapper is-fr">
                        Do not supply - FR
                        <Button className="btn btn--disc" onClick={() => { openPatientTemplateDoNotSupplyModal(patientTemplate) }} disabled={pageViewStore.disabled}>
                          <i className="u-icon u-icon--xxsml u-icon--close--neg"></i>
                        </Button>
                      </div>
                    </div>
                  }
                  {patientTemplate.isSystemDns &&
                    <div>
                      <div className="u-mar--left--xsml btn--disc__wrapper is-fr">
                        Do not supply
                      </div>
                    </div>
                  }
                  {patientTemplate.missingProductNote && patientTemplate.missingProductNote.trim() &&
                    <React.Fragment>
                      <ReactTooltip positionStrategy="fixed" id={"patientTemplateRedMissingProductTooltip" + patientTemplate.id} place="bottom" />
                      <Button className="btn btn--status--red u-mar--left--xsml" icon="u-icon u-icon--med-lrg u-icon--missing-product" onClick={() => { openPatientTemplateMissingProductNoteModal(patientTemplate) }} disabled={pageViewStore.disabled} data-tooltip-id={"patientTemplateRedMissingProductTooltip" + patientTemplate.id} data-tooltip-content="Missing Product" />
                    </React.Fragment>
                  }
                  <div className="card--tertiary__header__actions u-align--h--right">
                    {pageViewStore.nikoPatientDetail.eligibilityStatus && pageViewStore.nikoPatientDetail.eligibilityStatus.name === 'Eligible' &&
                      <div className="btn--group status--sqare__wrapper" data-tooltip-content="View Note">
                        <BaasicButton type="button" className="btn btn--base-plus btn--light btn--uppercase niko" disabled={!patientTemplate.clinicalStatus} label={patientTemplate.clinicalStatus && patientTemplate.clinicalStatus.name || "Unselected"} />
                      </div>
                    }
                    <div className="btn--group">
                      {pageViewStore.nikoPatientDetail.eligibilityStatus && pageViewStore.nikoPatientDetail.eligibilityStatus.name === 'Eligible' && <div className="btn--group__divider"></div>}
                      <Button className="btn no-hover" icon="u-icon u-icon--med u-icon--us-question-note" style={{ display: "none" }} />
                      <div className="btn--group status--sqare__wrapper" data-tooltip-content="View Note" style={{ display: "none" }}>
                        <Button className="btn no-hover" icon={"u-icon u-icon--med u-icon--do-not-supply"} />
                      </div>
                      <div className="btn--group status--sqare__wrapper" style={{ display: "none" }}>
                        <Button className="btn no-hover" icon="u-icon u-icon--med u-icon--danger--primary" />
                      </div>
                      <div className="btn--group status--sqare__wrapper">
                        {(!patientTemplate.missingProductNote || !patientTemplate.missingProductNote.trim()) &&
                          <React.Fragment>
                            <ReactTooltip positionStrategy="fixed" id={"patientTemplateMissingProductTooltip" + patientTemplate.id} place="bottom" />
                            <Button
                              className="btn no-hover"
                              icon="u-icon u-icon--med-lrg u-icon--us-question-note"
                              onClick={() => { openPatientTemplateMissingProductNoteModal(patientTemplate) }}
                              disabled={pageViewStore.disabled || disabledItem}
                              data-tooltip-id={"patientTemplateMissingProductTooltip" + patientTemplate.id}
                              data-tooltip-content="Missing Product"
                            />
                          </React.Fragment>
                        }
                        {!patientTemplate.isUserDns &&
                          <React.Fragment>
                            <ReactTooltip positionStrategy="fixed" id={"patientTemplateMarkAsDoNotSupplyTooltip" + patientTemplate.id} place="bottom" />
                            <Button
                              className="btn no-hover"
                              icon="u-icon u-icon--med-lrg u-icon--do-not-supply"
                              onClick={() => { openPatientTemplateDoNotSupplyModal(patientTemplate) }}
                              disabled={pageViewStore.disabled}
                              data-tooltip-id={"patientTemplateMarkAsDoNotSupplyTooltip" + patientTemplate.id}
                              data-tooltip-content="Mark As Do Not Supply"
                            />
                          </React.Fragment>
                        }
                        {!patientTemplate.isNew && !patientTemplate.isDiscontinued &&
                          <React.Fragment>
                            <ReactTooltip positionStrategy="fixed" id={"patientTemplateDiscontinuedTooltip" + patientTemplate.id} place="bottom" />
                            <Button
                              className="btn no-hover"
                              icon="u-icon u-icon--med-lrg u-icon--danger--primary"
                              onClick={() => changeDiscontinuedStatus(patientTemplate)}
                              disabled={pageViewStore.disabled}
                              data-tooltip-id={"patientTemplateDiscontinuedTooltip" + patientTemplate.id}
                              data-tooltip-content="Discontinued"
                            />
                          </React.Fragment>
                        }
                        {patientTemplate.isNew &&
                          <Button
                            className="btn no-hover"
                            icon="u-icon u-icon--med u-icon--delete--brand"
                            onClick={() => openFacilityOrderTemplateNewFlowDeleteModal(patientTemplate)}
                            disabled={pageViewStore.disabled || disabledItem}
                            data-tooltip-id={"patientTemplateTooltip" + patientTemplate.id}
                            data-tooltip-content="Delete Template"
                          />
                        }
                      </div>
                    </div>
                    <div className="status--sqare__wrapper">
                      <BaasicButton type="button" className={`btn btn--base btn--light ${
                        patientTemplate.isDiscontinued || pageViewStore.nikoPatientDetail.status === 'Discharged' || pageViewStore.nikoPatientDetail.status === 'Expired'
                          ? "is-grey"
                          : patientTemplate.isNew
                            ? "is-yellow"
                            : patientTemplate.intakeStatusId === pageViewStore.noChangeStatusId
                              ? "is-green"
                              : patientTemplate.intakeStatusId === pageViewStore.changeStatusId
                                ? "is-orange"
                                : "is-grey"
                      } is-nonclickable`} label={patientTemplate.portalId.toString()} showTooltip={false} />
                    </div>
                  </div>
                </div>
              </th>
            </tr>
            <tr>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
              {showItemQuantitiesColumn() && <th className="table__label--sml"><span className="table__label--inner">TEMP QTY</span></th>}
              {showItemQuantitiesColumn() && <th className="table__label--sml"><span className="table__label--inner">SO QTY</span></th>}
            </tr>
          </thead>
          <tbody className="tbody-niko" style={patientTemplate.isUserDns || patientTemplate.isSystemDns ? { opacity: "0.5" } : {}}>
            {patientTemplate.patientTemplateItems.map((item) => {
              if (!item.product && item.toggle) {
                setTimeout(() => {
                  if (dropdownRef.current) {
                    // v14 dropped the inner .k-dropdown-wrap — the DropDownList root element is the clickable toggle
                    const toggle = dropdownRef.current.element;
                    if (toggle) {
                      setItemToggle(item);
                      toggle.click();
                    }
                  }
                }, 0);
              }
              return (
                <tr key={item.id} className={`${(!patientTemplate.isNew && item.actionId === insertImportActionId && !pageViewStore.nikoPatientDetail.isNew) ? "new-row" : (item.actionId === deleteImportActionId ? "deleted-row" : "")} ${item.product ? "" : "is-focused"}`}>
                  <td className="table-col-input is-flex">
                    {item.product ? (
                      <React.Fragment>
                        {item.product.description}{" "}
                        <span className="table__icons-wrapper">
                          {!item.product.billableToMedicare && (
                            <i className="u-icon u-icon--sml u-icon--non-billable" />
                          )}{" "}
                          {(item.isBackordered != null ? item.isBackordered : item.product.backordered) && (
                            <i className="u-icon u-icon--med-lrg u-icon--backordered" />
                          )}
                        </span>
                      </React.Fragment>
                    ) : (
                      <BaasicDropdown
                        placeholder="Select item name..."
                        store={patientTemplate.productStore || productStore}
                        value={item.product}
                        className="input--table"
                        itemRender={productDropdownItemRender}
                        customRef={dropdownRef}
                        onChange={(e) => saveItemOnProductSelect(item, e.target.value)}
                        disabled={pageViewStore.disabled}
                      />
                    )}
                  </td>
                  <td>{item.product && item.product.itemId}</td>
                  <td className="table-col-input text-align-left">{item.procedureCode}</td>
                  <td className="table-col-input text-align-left has-input" id={"templateQuantity-" + patientTemplate.id}>
                    {!patientTemplate.isNew && !pageViewStore.nikoPatientDetail.isNew && item.initialPatientTemplateItem && item.initialPatientTemplateItem.Quantity != item.savedQuantity && item.actionId !== insertImportActionId &&
                      <React.Fragment>
                        <ReactTooltip positionStrategy="fixed" id={"registerTipQuantity" + item.id} place="top" delayShow={600} render={() =>
                          <TooltipContent initianValue={item.initialPatientTemplateItem && item.initialPatientTemplateItem.Quantity} history={item.history} fieldName={"Quantity"} />} />
                        <div className="table-col-input__notif opacity-50" data-tooltip-id={"registerTipQuantity" + item.id} onMouseEnter={async () => await getFieldHistory(item, 'Quantity', item.savedQuantity)} data-tooltip-content=""></div>
                      </React.Fragment>
                    }
                    {item.product && (
                      <input
                        className="input--table input--inherit-color no-arrows input--text-align--left w--70--px"
                        onClick={(e) => { e.target.select(); showItemQuantities(patientTemplate, true); }}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        value={item.quantity}
                        onChange={(e) => {
                          editItem(item, parseInt(e.target.value), "quantity");
                        }}
                        onBlur={() => {
                          updateItem(item, patientTemplate);
                        }}
                        type="number"
                        min="0"
                        disabled={pageViewStore.disabled || disabledItem}
                      />
                    )}
                  </td>
                  {showItemQuantitiesColumn() &&
                    <td className="table-col-input text-align-left has-input" id={"orderQuantity-" + patientTemplate.id}>
                      {!patientTemplate.isNew && !pageViewStore.nikoPatientDetail.isNew && item.initialPatientTemplateItem && item.initialPatientTemplateItem.SalesOrderQuantity != item.savedSalesOrderQuantity && item.actionId !== insertImportActionId &&
                        <React.Fragment>
                          <ReactTooltip positionStrategy="fixed" id={"registerTipSalesOrderQuantity" + item.id} place="top" delayShow={600} render={() =>
                            <TooltipContent initianValue={item.initialPatientTemplateItem && item.initialPatientTemplateItem.SalesOrderQuantity} history={item.history} fieldName={"SalesOrderQuantity"} />} />
                          <div className="table-col-input__notif opacity-50" data-tooltip-id={"registerTipSalesOrderQuantity" + item.id} onMouseEnter={async () => await getFieldHistory(item, 'SalesOrderQuantity', item.savedSalesOrderQuantity)} data-tooltip-content=""></div>
                        </React.Fragment>
                      }
                      {item.product && (
                        <input
                          className="input--table input--inherit-color no-arrows input--text-align--left w--70--px"
                          onClick={(e) => { e.target.select(); showItemQuantities(patientTemplate, true); }}
                          onKeyDown={handleKeyDown}
                          onPaste={handlePaste}
                          value={item.salesOrderQuantity}
                          onChange={(e) => {
                            editItem(item, parseInt(e.target.value), "salesOrderQuantity");
                          }}
                          onBlur={() => {
                            updateItem(item, patientTemplate);
                          }}
                          type="number"
                          min="0"
                          disabled={pageViewStore.disabled || disabledItem}
                        />
                      )}
                    </td>
                  }
                  <td className="table-col-input">
                    {!patientTemplate.isNew && !pageViewStore.nikoPatientDetail.isNew && item.initialPatientTemplateItem && item.initialPatientTemplateItem.Note != item.savedNote && item.actionId !== insertImportActionId &&
                      <React.Fragment>
                        <ReactTooltip positionStrategy="fixed" id={"registerTipNote" + item.id} place="top" delayShow={600} render={() =>
                          <TooltipContent initianValue={item.initialPatientTemplateItem && item.initialPatientTemplateItem.Note} history={item.history} fieldName={"Note"} />} />
                        <div className="table-col-input__notif opacity-50" data-tooltip-id={"registerTipNote" + item.id} onMouseEnter={async () => await getFieldHistory(item, 'Note', item.savedNote)} data-tooltip-content=""></div>
                      </React.Fragment>
                    }
                    {item.product && (item.product.itemTypeId || (item.product.procedureCode_ && item.product.procedureCode_.abrv === "B4155")) ? (
                      <input
                        className="input--table input--inherit-color no-arrows input--text-align--left w--100 u-mar--left--tny"
                        value={item.note}
                        onClick={() => openNikoPatientTemplateItemCalculationModal(item, patientTemplate)}
                        onChange={() => { }}
                        readOnly={true}
                        disabled={pageViewStore.disabled || disabledItem}
                      />
                    ) : (
                      <input
                        className="input--table input--inherit-color no-arrows input--text-align--left w--100 u-mar--left--tny"
                        onClick={(e) => e.target.select()}
                        onChange={(e) => item.note = e.target.value}
                        onBlur={() => updateItem(item, patientTemplate)}
                        value={item.note || ""}
                        disabled={pageViewStore.disabled || disabledItem}
                      />
                    )}
                  </td>

                  <td className="type--center is-hidden">
                    <div className="table__icons">
                      {item.actionId !== insertImportActionId &&
                        <Button
                          disabled={pageViewStore.disabled || disabledItem || pageViewStore.nikoPatientDetail.disabled}
                          className="btn btn--icon"
                          icon="u-icon u-icon--update-table u-icon--sml"
                          label="Change"
                          title={"Swap"}
                          onClick={() => swapPatientTemplateItem(item, patientTemplate)}
                        ></Button>
                      }
                      {item.actionId === deleteImportActionId ?
                        <Button
                          className="btn btn--icon is-delete"
                          icon="u-icon u-icon--sml u-icon--undo--table"
                          label="Undelete"
                          onClick={() => setAction(item, updateImportActionId, patientTemplate)}
                          disabled={pageViewStore.disabled || disabledItem || pageViewStore.nikoPatientDetail.disabled}
                          title={"Undo"}
                        ></Button>
                        :
                        <Button
                          className="btn btn--icon is-delete"
                          icon={`u-icon u-icon--sml ${!item.product ? "u-icon--close--table" : "u-icon--delete--primary"}`}
                          onClick={() => { deletePatientTemplateItem(patientTemplate, item, true) }}
                          disabled={pageViewStore.disabled || disabledItem || pageViewStore.nikoPatientDetail.disabled}
                          title={!item.product ? "Cancel" : "Delete"}
                        ></Button>
                      }
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!(patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some((x) => !x.productId)) && (
          <button
            type="button"
            className={`table--secondary__add-button niko ${pageViewStore.disabled || disabledItem ? " is-disabled" : ""}`}
            onClick={() => addEmptyItem(patientTemplate)}
            disabled={pageViewStore.disabled || disabledItem}
          >
            <span></span>
          </button>
        )}
        </div>

        <CollapsibleSection title="Clinical Info" defaultCollapsed={true}>
          <NikoPatientTemplateClinicalEdit pageViewStore={nikoPatientTemplateListViewStore} patientTemplate={patientTemplate} isPso={true} />
        </CollapsibleSection>
        <BaasicConfirmModal modalParams={templateChangeConfirmModal} />
      </div>
    </div>
  );
});

NikoPatientTemplate.propTypes = {
  nikoPatientTemplateListViewStore: PropTypes.object.isRequired,
  patientTemplate: PropTypes.object.isRequired
};

class CollapsibleSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = { collapsed: props.defaultCollapsed !== undefined ? props.defaultCollapsed : true };
  }

  toggle = () => {
    this.setState(function (prevState) {
      return { collapsed: !prevState.collapsed };
    });
  };

  render() {
    const children = this.props.children;
    const collapsed = this.state.collapsed;

    return (
      <div className="col col-sml-12 col-lrg-12">
        <div
          className="u-mar--bottom--sml"
          style={{ display: "flex", alignItems: "center", cursor: "pointer", marginTop: "16px", width: "fit-content", gap: "10px" }}
          onClick={this.toggle}
        >
          <span className={!collapsed ? "u-icon u-icon--med u-icon--arrow-up " : "u-icon u-icon--med u-icon--arrow-down"}></span>
          <h4 className="">Clinical Info</h4>
        </div>
        {!collapsed && <div>{children}</div>}
      </div>
    );
  }
}

CollapsibleSection.propTypes = {
  title: PropTypes.string.isRequired,
  defaultCollapsed: PropTypes.bool,
  children: PropTypes.node,
};

export default defaultTemplate(NikoPatientTemplateListTemplate);
